// src/components/auth/ForgotPasswordModal.jsx
// Modal reutilizable para "¿Olvidaste tu contraseña?" en todos los logins
import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Mail, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Ingresa tu correo electrónico');
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await resetPassword(email);
      if (resetError) {
        setError(resetError);
      } else {
        setSent(true);
      }
    } catch {
      setError('Error al enviar el correo. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSent(false);
    setLoading(false);
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={handleClose} style={styles.closeBtn} aria-label="Cerrar">
          <X size={20} />
        </button>

        {sent ? (
          <>
            <div style={styles.iconWrap}>
              <CheckCircle size={48} color="#22c55e" />
            </div>
            <h3 style={styles.title}>¡Correo enviado!</h3>
            <p style={styles.subtitle}>
              Revisa tu bandeja de entrada en <strong>{email}</strong>. 
              Haz click en el enlace para restablecer tu contraseña.
            </p>
            <p style={styles.hint}>
              Si no lo ves, revisa la carpeta de spam.
            </p>
            <button type="button" onClick={handleClose} style={styles.btnPrimary}>
              Entendido
            </button>
          </>
        ) : (
          <>
            <div style={styles.iconWrap}>
              <Mail size={40} color="#18181b" />
            </div>
            <h3 style={styles.title}>Recuperar contraseña</h3>
            <p style={styles.subtitle}>
              Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
            </p>

            {error && (
              <div style={styles.errorBox}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                style={styles.input}
                disabled={loading}
                autoFocus
              />
              <button
                type="submit"
                style={{ ...styles.btnPrimary, opacity: loading ? 0.7 : 1 }}
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Estilos ───
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px 28px',
    maxWidth: '400px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
  },
  closeBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '4px',
    display: 'flex',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  title: {
    textAlign: 'center',
    fontSize: '1.2rem',
    fontWeight: '700',
    color: '#18181b',
    margin: '0 0 8px',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '0 0 20px',
    lineHeight: '1.5',
  },
  hint: {
    textAlign: 'center',
    fontSize: '0.78rem',
    color: '#9ca3af',
    margin: '0 0 20px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#dc2626',
    fontSize: '0.83rem',
    marginBottom: '16px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    width: '100%',
    padding: '11px',
    background: '#18181b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default ForgotPasswordModal;
